import { Upload } from 'lucide-react';
import { InfoDot } from '../hiring/HiringForm';
import { InlineLoader } from '../loading';
import { BulkRecipientsModal } from './BulkRecipientsModal';
import { DetailRulesField } from './DetailRulesField';
import { PeoplePicker } from './PeoplePicker';
import { TEAM_SCOPES, type RecipientMode } from './bannerOptions';
import { FieldLabel, PillRow } from './bannerFormBits';
import type { BannerAudience } from './useBannerAudience';

const MODES: { value: RecipientMode; label: string }[] = [
  { value: 'employees', label: 'Employees' },
  { value: 'managers', label: 'Teams' },
  { value: 'project', label: 'Everyone' },
  { value: 'details', label: 'By details' },
];

/** Who gets it: named people, whole reporting teams, the project's whole roster, or By details. */
export function SendToField({
  audience,
  counted = null,
}: {
  audience: BannerAudience;
  /** How many this audience comes to, when the server has counted it: beats the picker's guess. */
  counted?: number | null;
}) {
  const {
    selectedProjectId,
    recipientMode,
    setRecipientMode,
    selectedEmpCodes,
    setSelectedEmpCodes,
    selectedManagerCodes,
    setSelectedManagerCodes,
    teamScope,
    setTeamScope,
    employeeOptions,
    managerOptions,
    pickersLoading,
    employeesError,
    managersError,
    retryPickers,
    setShowBulkUpload,
    estimatedDirectReach,
  } = audience;
  const managers = selectedManagerCodes.length;
  const reach = (managers + estimatedDirectReach).toLocaleString();
  const rosterCount = counted ?? employeeOptions.length;
  const retry = (message: string) => (
    <p className="mt-1 text-xs text-red-600">
      {message}{' '}
      <button type="button" onClick={retryPickers} className="font-medium text-primary-600 underline hover:text-primary-700">
        Try again
      </button>
    </p>
  );

  return (
    <div>
      {selectedProjectId == null && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">
          Pick a project first — the recipient lists are project-scoped.
        </div>
      )}

      <FieldLabel text="Send to" />
      <PillRow options={MODES} value={recipientMode} onChange={setRecipientMode} />

      <div className="mt-2">
        {/* The whole roster needs no picking — and no picker, whose 200-row cap would
            suggest a partial list. The server resolves the roster itself when the send runs. */}
        {recipientMode === 'employees' ? (
          <>
            <PeoplePicker
              options={employeeOptions}
              selected={selectedEmpCodes}
              onChange={setSelectedEmpCodes}
              loading={pickersLoading}
              emptyText="Pick employees"
              placeholder="Search employees by name or code…"
              emptyHint={employeesError ?? 'No employees found for this project.'}
            />
            {employeesError && retry(employeesError)}
            <button
              type="button"
              onClick={() => setShowBulkUpload(true)}
              className="mt-1.5 flex w-fit items-center gap-1 text-[13px] font-medium text-primary-600 hover:text-primary-700"
            >
              <Upload className="h-3.5 w-3.5" />
              Add from an Excel list
            </button>
            <BulkRecipientsModal audience={audience} />
          </>
        ) : recipientMode === 'managers' ? (
          <>
            <PeoplePicker
              options={managerOptions}
              selected={selectedManagerCodes}
              onChange={setSelectedManagerCodes}
              loading={pickersLoading}
              emptyText="Pick managers"
              placeholder="Search managers by name or code…"
              emptyHint={managersError ?? 'No managers found for this project.'}
            />
            {managersError && retry(managersError)}
            {/* How deep to reach. The org is Trade Head → AM → iPro, so "their team" is a
                handful for an AM and hundreds for a Trade Head — the choice has to be explicit. */}
            <div className="mt-2">
              <FieldLabel
                text="How far down the hierarchy?"
                hint={TEAM_SCOPES.find((s) => s.value === teamScope)?.hint}
              />
              <PillRow
                small
                options={TEAM_SCOPES.map((s) => ({ value: s.value, label: s.label, title: s.hint }))}
                value={teamScope}
                onChange={setTeamScope}
              />
            </div>
            {/* Picking managers hides the real size of the send — 38 rows in this list can
                be thousands of people. Estimated from the team sizes shown per row. */}
            <p className="mt-1.5 flex items-center gap-1 text-[13px] text-gray-500">
              <span>
                {managers} manager{managers === 1 ? '' : 's'} selected.
                {counted != null
                  ? ` ${counted.toLocaleString()} ${counted === 1 ? 'person' : 'people'}.`
                  : managers > 0 && teamScope === 'direct'
                  ? ` About ${reach} people.`
                  : managers > 0 && teamScope === 'all'
                  ? ` At least ${reach} people.`
                  : ''}
              </span>
              <InfoDot
                text={
                  teamScope === 'all'
                    ? 'The manager always receives it too. A full hierarchy is likely more than this, since levels below the direct reports are included too.'
                    : 'The manager always receives it too.'
                }
              />
            </p>
            {/* A phone has no hover, and this one changes who is sent it. */}
            <p className="mt-1 text-xs text-gray-500 lg:hidden">The manager always receives it too.</p>
          </>
        ) : recipientMode === 'details' ? (
          <DetailRulesField audience={audience} />
        ) : (
          <>
            <p className="flex items-center gap-1 text-[13px] text-gray-500">
              {selectedProjectId == null ? (
                'Pick a project to send to its whole roster.'
              ) : pickersLoading && counted == null ? (
                <InlineLoader label="Counting the roster…" className="flex" />
              ) : rosterCount > 0 ? (
                // Only what the server has counted is exact: the picker's list is another read.
                <span>
                  {counted == null && 'About '}
                  <strong className="text-sm font-semibold text-gray-900">
                    {rosterCount.toLocaleString()}
                  </strong>{' '}
                  {rosterCount === 1 ? 'person' : 'people'} on this project
                </span>
              ) : (
                "Everyone on this project's roster."
              )}
              {/* Rows are written per person at send time — say so, or an admin reasonably
                  assumes a new joiner will get it too. */}
              <InfoDot text="Goes to whoever is on the roster when you send. People who join the project later won't get it." />
            </p>
            {/* A phone has no hover, and this one changes what someone would decide. */}
            <p className="mt-1 text-xs text-gray-500 lg:hidden">People who join later won't get it.</p>
          </>
        )}
      </div>
    </div>
  );
}
